/**
 * Framework-light pieces shared by the admin and client Company Detail pages:
 * the detail shape, the query-key builder, the pure IDR/date formatters, and the
 * `Field` display tile (raw JSX + brand CSS vars, no in-app divergent UI). The
 * pages keep their
 * own bodies in-app because they import per-app data layers (`../../api/client`,
 * `../../api/companies`) and the admin page layers on `refetchOnMount: "always"`,
 * the `ResendInviteAction` (uses `@kkp/ui` Button + toast),
 * and the optional `picStatus` field — so the react-query/data wiring and the
 * page JSX intentionally stay in each app.
 */

export type { CompanyDetail } from "@kkp/api-client";

export const COMPANY_DETAIL_KEY = (id: string) => ["company-detail", id];

/**
 * Format a MoneyIdr *string* (the wire money type) as grouped IDR. Parses the
 * string exactly once here, at display, and rejects a non-finite / non-numeric
 * value (returns "") so money never renders as "RpNaN".
 */
export function formatIdr(value: string | null | undefined): string {
  if (value === null || value === undefined) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatDateTime(value: string): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-[var(--kkp-radius)] border border-[color:var(--kkp-border)] bg-[color:var(--kkp-surface)] px-3 py-3 shadow-sm">
      <div className="text-[11px] font-semibold text-[color:var(--kkp-text-muted)]">
        {label}
      </div>
      <div className="mt-1 text-sm font-bold text-[color:var(--kkp-text)]">
        {value}
      </div>
    </div>
  );
}
